import { useMemo } from 'react';

export type LicenseAvailabilityMode = 'count' | 'unlimited' | 'not_on_plan';

export interface LicensePool {
  /** `true` means unlimited, which is how the training pool reports it. */
  count?: number | boolean;
  /** Already netted into `count` server-side; not used here. */
  reserved?: number;
  plan_eligible?: boolean;
}

export interface LicenseAvailability {
  value: number | null;
  mode: LicenseAvailabilityMode;
}

// The only place that decides between a count, unlimited and a call to action; availability
// displays should read this.
const useLicenseAvailability = (pool?: LicensePool | null): LicenseAvailability =>
  useMemo(() => {
    // Undefined `plan_eligible` counts as eligible; only an explicit `false` is not on the plan.
    if (!pool || pool.plan_eligible === false) {
      return { value: null, mode: 'not_on_plan' };
    }
    if (pool.count === true) {
      return { value: null, mode: 'unlimited' };
    }
    // `count` is already net of assigned and reserved seats, so it isn't reduced again here.
    return { value: Math.max(typeof pool.count === 'number' ? pool.count : 0, 0), mode: 'count' };
  }, [pool?.plan_eligible, pool?.count, Boolean(pool)]);

export default useLicenseAvailability;
